import React, { useEffect, useState } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  Card,
} from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import Swal from "sweetalert2";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

export default function EditSchoolEnrollment() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [families, setFamilies] = useState([]);
  const [children, setChildren] = useState([]);
  const [cities, setCities] = useState([]);
  const [donors, setDonors] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [formErrors, setFormErrors] = useState({});

  const [form, setForm] = useState({
    person_id: "",
    student_id: "",
    dob: "",
    admission_date: "",
    school_id: "",
    class_id: "",
    academic_session_id: "",
    roll_no: "",
    status: "",
    remarks: "",
    donor_id: "",
    fee: "",
    cost: "",

    student_image: null,
    going_image: null,
    fee_receipt: null,
    cost_receipt: null,
  });

  const [existingFiles, setExistingFiles] = useState({
    student_image: null,
    going_image: null,
    fee_receipt: null,
    cost_receipt: null,
    video: null,
  });

  const [previews, setPreviews] = useState({
    student_image: null,
    going_image: null,
  });

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common["Authorization"] =
    `Bearer ${AuthToken}`;

  const API_URL = process.env.REACT_APP_API_URL;

  /*
  |--------------------------------------------------------------------------
  | Initial Load
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      /*
       * Load initial dropdown data
       */
      const initialRes = await axios.get(
        `${API_URL}/initial-school-enrollments`
      );

      setFamilies(initialRes.data.people || []);
      setDonors(initialRes.data.donors || []);
      setCities(initialRes.data.cities || []);
      setSchools(initialRes.data.schools || []);
      setClasses(initialRes.data.schoolClasses || []);
      setSessions(initialRes.data.accesedSessions || []);

      /*
       * Load existing enrollment
       */
      const enrollmentRes = await axios.get(
        `${API_URL}/school-enrollments/${id}`
      );

      const enrollment =
        enrollmentRes.data.data;

      if (!enrollment) {
        throw new Error(
          "Enrollment data not found."
        );
      }

      /*
       * Load students belonging to this family
       */
      if (enrollment.student?.person_id) {
        const studentRes = await axios.get(
          `${API_URL}/initial-school-enrollments/${enrollment.student.person_id}`
        );

        setChildren(
          studentRes.data.students || []
        );
      }

      /*
       * Filter classes according to school
       */
      const schoolClasses =
        (initialRes.data.schoolClasses || []).filter(
          (item) =>
            Number(item.school_id) ===
            Number(enrollment.school_id)
        );

      setFilteredClasses(schoolClasses);

      /*
       * Populate form
       */
      setForm({
        person_id:
          enrollment.student?.person_id ||
          "",

        student_id:
          enrollment.student_id || "",

        dob:
          enrollment.student?.dob || "",

        admission_date:
          enrollment.admission_date
            ? enrollment.admission_date.substring(
                0,
                10
              )
            : "",

        school_id:
          enrollment.school_id || "",

        class_id:
          enrollment.class_id || "",

        academic_session_id:
          enrollment.academic_session_id || "",

        roll_no:
          enrollment.roll_no || "",

        status:
          enrollment.status || "",

        remarks:
          enrollment.remarks || "",

        donor_id:
          enrollment.donor_id || "",

        fee:
          enrollment.fee || "",

        cost:
          enrollment.cost || "",

        student_image: null,
        going_image: null,
        fee_receipt: null,
        cost_receipt: null,
      });

      /*
       * Existing files
       */
      setExistingFiles({
        student_image:
          enrollment.student_image || null,

        going_image:
          enrollment.going_image || null,

        fee_receipt:
          enrollment.fee_receipt || null,

        cost_receipt:
          enrollment.cost_receipt || null,

        video:
          enrollment.video || null,
      });

    } catch (error) {
      console.error(
        "Edit Enrollment Error:",
        error
      );

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to load school enrollment.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Family Options
  |--------------------------------------------------------------------------
  */

  const familyOptions = families.map((f) => ({
    value: f.person_id,
    label: `${f.f_number} - ${f.beneficiary_name} (${f.city_name})`,
  }));

  /*
  |--------------------------------------------------------------------------
  | Family Change
  |--------------------------------------------------------------------------
  */

  const handleFamilyChange = async (
    selected
  ) => {
    const familyId =
      selected?.value || "";

    setForm((prev) => ({
      ...prev,
      person_id: familyId,
      student_id: "",
      dob: "",
    }));

    setFormErrors((prev) => ({
      ...prev,
      person_id: "",
      student_id: "",
      dob: "",
    }));

    if (!familyId) {
      setChildren([]);
      return;
    }

    try {
      const res = await axios.get(
        `${API_URL}/initial-school-enrollments/${familyId}`
      );

      setChildren(
        res.data.students || []
      );
    } catch (error) {
      console.error(error);

      setChildren([]);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | General Change
  |--------------------------------------------------------------------------
  */

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    /*
     * School changed
     */
    if (name === "school_id") {
      const filtered =
        classes.filter(
          (item) =>
            Number(item.school_id) ===
            Number(value)
        );

      setFilteredClasses(
        filtered
      );

      setForm((prev) => ({
        ...prev,
        school_id: value,
        class_id: "",
      }));

      setFormErrors((prev) => ({
        ...prev,
        class_id: "",
      }));
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Student Change
  |--------------------------------------------------------------------------
  */

  const handleChildChange = (
    e
  ) => {
    const studentId =
      e.target.value;

    const selected =
      children.find(
        (c) =>
          Number(c.id) ===
          Number(studentId)
      );

    setForm((prev) => ({
      ...prev,
      student_id:
        selected?.id || "",
      dob:
        selected?.dob || "",
    }));

    setFormErrors((prev) => ({
      ...prev,
      student_id: "",
      dob: "",
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | File Change
  |--------------------------------------------------------------------------
  */

  const handleFileChange = (
    e,
    field
  ) => {
    const file =
      e.target.files?.[0];

    if (!file) return;

    setForm((prev) => ({
      ...prev,
      [field]: file,
    }));

    /*
     * Image preview
     */
    if (
      field ===
        "student_image" ||
      field ===
        "going_image"
    ) {
      if (previews[field]) {
        URL.revokeObjectURL(
          previews[field]
        );
      }

      const url =
        URL.createObjectURL(
          file
        );

      setPreviews((prev) => ({
        ...prev,
        [field]: url,
      }));
    }

    /*
     * Remove existing file display
     * because a new one is selected.
     */
    setExistingFiles((prev) => ({
      ...prev,
      [field]: null,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | Remove New Image
  |--------------------------------------------------------------------------
  */

  const removeImage = (
    field
  ) => {
    if (previews[field]) {
      URL.revokeObjectURL(
        previews[field]
      );
    }

    setForm((prev) => ({
      ...prev,
      [field]: null,
    }));

    setPreviews((prev) => ({
      ...prev,
      [field]: null,
    }));

    const input =
      document.getElementById(
        `input-${field}`
      );

    if (input) {
      input.value = "";
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Validation
  |--------------------------------------------------------------------------
  */

  const validate = () => {
    const errors = {};

    if (!form.person_id) {
      errors.person_id =
        "Family is required";
    }

    if (!form.student_id) {
      errors.student_id =
        "Student is required";
    }

    if (!form.dob) {
      errors.dob =
        "Student date of birth is required";
    }

    if (!form.admission_date) {
      errors.admission_date =
        "Admission date is required";
    }

    if (!form.school_id) {
      errors.school_id =
        "School is required";
    }

    if (!form.class_id) {
      errors.class_id =
        "Class is required";
    }

    if (
      !form.academic_session_id
    ) {
      errors.academic_session_id =
        "Academic session is required";
    }

    if (!form.status) {
      errors.status =
        "Status is required";
    }

    if (
      form.fee !== "" &&
      Number(form.fee) < 0
    ) {
      errors.fee =
        "Fee cannot be negative";
    }

    if (
      form.cost !== "" &&
      Number(form.cost) < 0
    ) {
      errors.cost =
        "Cost cannot be negative";
    }

    setFormErrors(errors);

    return (
      Object.keys(errors)
        .length === 0
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Submit Update
  |--------------------------------------------------------------------------
  */

  const submitForm = async (
    e
  ) => {
    e.preventDefault();

    if (!validate()) {
      Swal.fire({
        title: "Error!",
        text: "Please fix the errors in the form.",
        icon: "error",
        confirmButtonText: "Ok",
      });

      return;
    }

    try {
      setSaving(true);

      const data =
        new FormData();

      /*
       * Laravel method spoofing
       */
      data.append(
        "_method",
        "PUT"
      );

      /*
       * Append normal fields
       */
      data.append(
        "student_id",
        form.student_id
      );

      data.append(
        "school_id",
        form.school_id
      );

      data.append(
        "class_id",
        form.class_id
      );

      data.append(
        "academic_session_id",
        form.academic_session_id
      );

      data.append(
        "admission_date",
        form.admission_date
      );

      data.append(
        "status",
        form.status
      );

      if (form.person_id) {
        data.append(
          "person_id",
          form.person_id
        );
      }

      if (form.donor_id) {
        data.append(
          "donor_id",
          form.donor_id
        );
      }

      if (form.roll_no) {
        data.append(
          "roll_no",
          form.roll_no
        );
      }

      if (form.dob) {
        data.append(
          "dob",
          form.dob
        );
      }

      if (form.fee !== "") {
        data.append(
          "fee",
          form.fee
        );
      }

      if (form.cost !== "") {
        data.append(
          "cost",
          form.cost
        );
      }

      if (form.remarks) {
        data.append(
          "remarks",
          form.remarks
        );
      }

      /*
       * New files only
       */
      if (
        form.student_image
      ) {
        data.append(
          "student_image",
          form.student_image
        );
      }

      if (
        form.going_image
      ) {
        data.append(
          "going_image",
          form.going_image
        );
      }

      if (
        form.fee_receipt
      ) {
        data.append(
          "fee_receipt",
          form.fee_receipt
        );
      }

      if (
        form.cost_receipt
      ) {
        data.append(
          "cost_receipt",
          form.cost_receipt
        );
      }

      const response =
        await axios.post(
          `${API_URL}/school-enrollments/${id}`,
          data,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

      Swal.fire({
        title: "Success!",
        text:
          response.data?.message ||
          "School enrollment updated successfully.",
        icon: "success",
        confirmButtonText:
          "Ok",
      }).then(() => {
        navigate(
          "/all-school-enrollment"
        );
      });

    } catch (error) {
      console.error(
        "Update Error:",
        error
      );

      if (
        error.response?.status ===
        422
      ) {
        setFormErrors(
          error.response.data
            .errors || {}
        );

        Swal.fire({
          title: "Validation Error!",
          text: "Please check the form fields.",
          icon: "error",
        });
      } else {
        Swal.fire({
          title: "Error!",
          text:
            error.response?.data
              ?.message ||
            "Failed to update school enrollment.",
          icon: "error",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Existing File URL
  |--------------------------------------------------------------------------
  */

  const fileUrl = (
    path
  ) => {
    if (!path) return null;

    /*
     * Change this if your Laravel
     * storage URL is different.
     */
    return `${API_URL.replace(
      /\/api\/?$/,
      ""
    )}/storage/${path}`;
  };

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <PageLayout>
        <Card>
          <Card.Body className="text-center py-5">
            <h5>
              Loading enrollment...
            </h5>
          </Card.Body>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout>

      {/* Breadcrumb */}
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">

              <h3 className="mc-breadcrumb-title">
                Edit School Enrollment
              </h3>

              <ul className="mc-breadcrumb-list">

                <li className="mc-breadcrumb-item">
                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                  >
                    home
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link
                    to="/school-enrollments"
                    className="mc-breadcrumb-link"
                  >
                    School Enrollments
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  Edit Enrollment
                </li>

              </ul>
            </div>
          </div>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Header>
          <h5 className="mb-0">
            Edit School Enrollment
          </h5>
        </Card.Header>

        <Card.Body>

          <Form
            onSubmit={
              submitForm
            }
          >

            <Row>

              {/* Enrollment Number */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Enrollment Number
                  </Form.Label>

                  <Form.Control
                    type="text"
                    value={
                      `ENR-${id}`
                    }
                    disabled
                  />

                </Form.Group>
              </Col>

              {/* Family */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Family
                  </Form.Label>

                  <Select
                    options={
                      familyOptions
                    }
                    placeholder="Select Family"
                    isSearchable
                    value={
                      familyOptions.find(
                        (option) =>
                          Number(
                            option.value
                          ) ===
                          Number(
                            form.person_id
                          )
                      ) ||
                      null
                    }
                    onChange={
                      handleFamilyChange
                    }
                  />

                  {formErrors.person_id && (
                    <div className="text-danger mt-1">
                      {
                        formErrors.person_id
                      }
                    </div>
                  )}

                </Form.Group>
              </Col>

              {/* Student */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Student
                  </Form.Label>

                  <Form.Select
                    value={
                      form.student_id
                    }
                    onChange={
                      handleChildChange
                    }
                    isInvalid={
                      !!formErrors.student_id
                    }
                  >

                    <option value="">
                      Select Student
                    </option>

                    {children.map(
                      (child) => (
                        <option
                          key={
                            child.id
                          }
                          value={
                            child.id
                          }
                        >
                          {
                            child.name
                          }
                        </option>
                      )
                    )}

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.student_id
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* DOB */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Student DOB
                  </Form.Label>

                  <Form.Control
                    type="date"
                    name="dob"
                    value={
                      form.dob
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.dob
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.dob
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Admission Date */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Admission Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    name="admission_date"
                    value={
                      form.admission_date
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.admission_date
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.admission_date
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* School */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    School
                  </Form.Label>

                  <Form.Select
                    name="school_id"
                    value={
                      form.school_id
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.school_id
                    }
                  >

                    <option value="">
                      Select School
                    </option>

                    {schools.map(
                      (school) => (
                        <option
                          key={
                            school.id
                          }
                          value={
                            school.id
                          }
                        >
                          {
                            school.level
                          }{" "}
                          {
                            school.authority
                          }{" "}
                          {
                            school.name
                          }
                          {school.city_name
                            ? ` (${school.city_name})`
                            : ""}
                        </option>
                      )
                    )}

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.school_id
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Class */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Class
                  </Form.Label>

                  <Form.Select
                    name="class_id"
                    value={
                      form.class_id
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.class_id
                    }
                  >

                    <option value="">
                      Select Class
                    </option>

                    {filteredClasses.map(
                      (item) => (
                        <option
                          key={
                            item.id
                          }
                          value={
                            item.id
                          }
                        >
                          {
                            item.name
                          }
                        </option>
                      )
                    )}

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.class_id
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Session */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Academic Session
                  </Form.Label>

                  <Form.Select
                    name="academic_session_id"
                    value={
                      form.academic_session_id
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.academic_session_id
                    }
                  >

                    <option value="">
                      Select Session
                    </option>

                    {sessions.map(
                      (session) => (
                        <option
                          key={
                            session.id
                          }
                          value={
                            session.id
                          }
                        >
                          {
                            session.name
                          }
                        </option>
                      )
                    )}

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.academic_session_id
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Roll No */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Roll Number
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="roll_no"
                    value={
                      form.roll_no
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter Roll Number"
                  />

                </Form.Group>
              </Col>

              {/* Status */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Status
                  </Form.Label>

                  <Form.Select
                    name="status"
                    value={
                      form.status
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.status
                    }
                  >

                    <option value="">
                      Select
                    </option>

                    <option value="active">
                      Active
                    </option>

                    <option value="completed">
                      Completed
                    </option>

                    <option value="dropped">
                      Dropped
                    </option>

                    <option value="transferred">
                      Transferred
                    </option>

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.status
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Donor */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Donor
                  </Form.Label>

                  <Form.Select
                    name="donor_id"
                    value={
                      form.donor_id
                    }
                    onChange={
                      handleChange
                    }
                  >

                    <option value="">
                      Select Donor
                    </option>

                    {donors.map(
                      (donor) => (
                        <option
                          key={
                            donor.id
                          }
                          value={
                            donor.id
                          }
                        >
                          {
                            donor.name
                          }
                        </option>
                      )
                    )}

                  </Form.Select>

                </Form.Group>
              </Col>

              {/* Fee */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Fee
                  </Form.Label>

                  <Form.Control
                    type="number"
                    min="0"
                    name="fee"
                    value={
                      form.fee
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.fee
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.fee
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Fee Receipt */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Fee Receipt
                  </Form.Label>

                  <Form.Control
                    type="file"
                    id="input-fee_receipt"
                    onChange={(
                      e
                    ) =>
                      handleFileChange(
                        e,
                        "fee_receipt"
                      )
                    }
                  />

                  {existingFiles.fee_receipt && (
                    <div className="mt-2">

                      <a
                        href={fileUrl(
                          existingFiles.fee_receipt
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Existing Receipt
                      </a>

                    </div>
                  )}

                </Form.Group>
              </Col>

              {/* Cost */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Material Cost
                  </Form.Label>

                  <Form.Control
                    type="number"
                    min="0"
                    name="cost"
                    value={
                      form.cost
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.cost
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.cost
                    }
                  </Form.Control.Feedback>

                </Form.Group>
              </Col>

              {/* Cost Receipt */}
              <Col md={6}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Cost Receipt
                  </Form.Label>

                  <Form.Control
                    type="file"
                    id="input-cost_receipt"
                    onChange={(
                      e
                    ) =>
                      handleFileChange(
                        e,
                        "cost_receipt"
                      )
                    }
                  />

                  {existingFiles.cost_receipt && (
                    <div className="mt-2">

                      <a
                        href={fileUrl(
                          existingFiles.cost_receipt
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Existing Receipt
                      </a>

                    </div>
                  )}

                </Form.Group>
              </Col>

              {/* Remarks */}
              <Col md={12}>
                <Form.Group className="mb-3">

                  <Form.Label>
                    Remarks
                  </Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="remarks"
                    value={
                      form.remarks
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter remarks..."
                  />

                </Form.Group>
              </Col>

            </Row>

            {/* Images */}
            <Row className="mt-2">

              {/* Student Image */}
              <Col md={6}>
                <Card className="mb-3">

                  <Card.Header>
                    Student Picture
                  </Card.Header>

                  <Card.Body>

                    <Form.Control
                      id="input-student_image"
                      type="file"
                      accept="image/*"
                      onChange={(
                        e
                      ) =>
                        handleFileChange(
                          e,
                          "student_image"
                        )
                      }
                    />

                    {previews.student_image ? (
                      <div className="mt-3 text-center">

                        <img
                          src={
                            previews.student_image
                          }
                          alt="New Student"
                          style={{
                            width:
                              "100%",
                            maxHeight:
                              "250px",
                            objectFit:
                              "cover",
                            borderRadius:
                              "10px",
                          }}
                        />

                        <Button
                          variant="danger"
                          size="sm"
                          className="mt-2"
                          onClick={() =>
                            removeImage(
                              "student_image"
                            )
                          }
                        >
                          Remove New Image
                        </Button>

                      </div>
                    ) : existingFiles.student_image ? (
                      <div className="mt-3 text-center">

                        <img
                          src={fileUrl(
                            existingFiles.student_image
                          )}
                          alt="Student"
                          style={{
                            width:
                              "100%",
                            maxHeight:
                              "250px",
                            objectFit:
                              "cover",
                            borderRadius:
                              "10px",
                          }}
                        />

                        <p className="text-muted mt-2 mb-0">
                          Existing Image
                        </p>

                      </div>
                    ) : (
                      <p className="text-muted mt-3">
                        No image uploaded.
                      </p>
                    )}

                  </Card.Body>

                </Card>
              </Col>

              {/* Going Image */}
              <Col md={6}>
                <Card className="mb-3">

                  <Card.Header>
                    Going to School / Institute
                  </Card.Header>

                  <Card.Body>

                    <Form.Control
                      id="input-going_image"
                      type="file"
                      accept="image/*"
                      onChange={(
                        e
                      ) =>
                        handleFileChange(
                          e,
                          "going_image"
                        )
                      }
                    />

                    {previews.going_image ? (
                      <div className="mt-3 text-center">

                        <img
                          src={
                            previews.going_image
                          }
                          alt="New Going"
                          style={{
                            width:
                              "100%",
                            maxHeight:
                              "250px",
                            objectFit:
                              "cover",
                            borderRadius:
                              "10px",
                          }}
                        />

                        <Button
                          variant="danger"
                          size="sm"
                          className="mt-2"
                          onClick={() =>
                            removeImage(
                              "going_image"
                            )
                          }
                        >
                          Remove New Image
                        </Button>

                      </div>
                    ) : existingFiles.going_image ? (
                      <div className="mt-3 text-center">

                        <img
                          src={fileUrl(
                            existingFiles.going_image
                          )}
                          alt="Going School"
                          style={{
                            width:
                              "100%",
                            maxHeight:
                              "250px",
                            objectFit:
                              "cover",
                            borderRadius:
                              "10px",
                          }}
                        />

                        <p className="text-muted mt-2 mb-0">
                          Existing Image
                        </p>

                      </div>
                    ) : (
                      <p className="text-muted mt-3">
                        No image uploaded.
                      </p>
                    )}

                  </Card.Body>

                </Card>
              </Col>

            </Row>

            {/* Submit */}
            <div className="mt-3 text-end">

              <Button
                variant="secondary"
                className="me-2"
                onClick={() =>
                  navigate(
                    "/school-enrollments"
                  )
                }
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="success"
                disabled={saving}
              >
                {saving
                  ? "Updating..."
                  : "Update Enrollment"}
              </Button>

            </div>

          </Form>

        </Card.Body>
      </Card>

      {/* Existing Video */}
      {existingFiles.video && (
        <Card className="mt-4">

          <Card.Header>
            School Enrollment Video
          </Card.Header>

          <Card.Body>

            <video
              controls
              style={{
                width:
                  "100%",
                maxHeight:
                  "500px",
                borderRadius:
                  "10px",
              }}
            >
              <source
                src={fileUrl(
                  existingFiles.video
                )}
                type="video/mp4"
              />

              Your browser does not support
              video playback.
            </video>

          </Card.Body>

        </Card>
      )}

    </PageLayout>
  );
}